import { MarketOutcome, PeriodType } from '../../enums';
import { MarketConfig } from '../../interfaces';
import {
	extractNumber,
	extractSelection,
	getPairPlayers,
	getPlayerStats,
	resolveCondition,
	resolveOvertimeGeneric,
	resolveTotalPointsOddEven,
	resolveMoneyline,
	resolveSpread,
	resolveTotalPoints,
	resolveTeamTotal,
	resolveFirstTeamToScore,
} from '../../utils';
import {
	buildFirstTeamToScoreQuestion,
	buildMoneylineQuestion,
	buildRunLineQuestion,
	buildTeamTotalQuestion,
	buildTotalRunsOddEvenQuestion,
	buildTotalRunsQuestion,
	getPeriodScores,
	resolveGamePlayerProp,
	resolveGamePlayerPropCombo,
} from '../../utils/market/mlb.utils';

export const mlbConfig: Record<string, MarketConfig> = {
	// Match Outcome
	Moneyline: {
		buildQuestion: (m) => buildMoneylineQuestion(m.selection, PeriodType.Game),
		isFinalRequired: true,
		resolve: (q, gc) => resolveMoneyline(getPeriodScores, q, gc, PeriodType.Game),
	},
	'1st Half Moneyline': {
		buildQuestion: (m) => buildMoneylineQuestion(m.selection, PeriodType.Half1),
		resolve: (q, gc) => resolveMoneyline(getPeriodScores, q, gc, PeriodType.Half1),
	},
	'1st 3 Innings Moneyline': {
		buildQuestion: (m) => buildMoneylineQuestion(m.selection, PeriodType.FirstPeriod3),
		resolve: (q, gc) => resolveMoneyline(getPeriodScores, q, gc, PeriodType.FirstPeriod3),
	},
	'1st 7 Innings Moneyline': {
		buildQuestion: (m) => buildMoneylineQuestion(m.selection, PeriodType.FirstPeriod7),
		resolve: (q, gc) => resolveMoneyline(getPeriodScores, q, gc, PeriodType.FirstPeriod7),
	},

	// Spread
	'Run Line': {
		buildQuestion: (m) => buildRunLineQuestion(m.selection, m.points, PeriodType.Game),
		isFinalRequired: true,
		resolve: (q, gc) => resolveSpread(getPeriodScores, q, gc, PeriodType.Game),
	},
	'1st 3 Innings Run Line': {
		buildQuestion: (m) => buildRunLineQuestion(m.selection, m.points, PeriodType.FirstPeriod3),
		resolve: (q, gc) => resolveSpread(getPeriodScores, q, gc, PeriodType.FirstPeriod3),
	},
	'1st 7 Innings Run Line': {
		buildQuestion: (m) => buildRunLineQuestion(m.selection, m.points, PeriodType.FirstPeriod7),
		resolve: (q, gc) => resolveSpread(getPeriodScores, q, gc, PeriodType.FirstPeriod7),
	},
	'1st Half Run Line': {
		buildQuestion: (m) => buildRunLineQuestion(m.selection, m.points, PeriodType.Half1),
		resolve: (q, gc) => resolveSpread(getPeriodScores, q, gc, PeriodType.Half1),
	},
	'1st Inning Run Line': {
		buildQuestion: (m) => buildRunLineQuestion(m.selection, m.points, PeriodType.Period1),
		resolve: (q, gc) => resolveSpread(getPeriodScores, q, gc, PeriodType.Period1),
	},
	'2nd Inning Run Line': {
		buildQuestion: (m) => buildRunLineQuestion(m.selection, m.points, PeriodType.Period2),
		resolve: (q, gc) => resolveSpread(getPeriodScores, q, gc, PeriodType.Period2),
	},
	'3rd Inning Run Line': {
		buildQuestion: (m) => buildRunLineQuestion(m.selection, m.points, PeriodType.Period3),
		resolve: (q, gc) => resolveSpread(getPeriodScores, q, gc, PeriodType.Period3),
	},
	'4th Inning Run Line': {
		buildQuestion: (m) => buildRunLineQuestion(m.selection, m.points, PeriodType.Period4),
		resolve: (q, gc) => resolveSpread(getPeriodScores, q, gc, PeriodType.Period4),
	},
	'5th Inning Run Line': {
		buildQuestion: (m) => buildRunLineQuestion(m.selection, m.points, PeriodType.Period5),
		resolve: (q, gc) => resolveSpread(getPeriodScores, q, gc, PeriodType.Period5),
	},
	'6th Inning Run Line': {
		buildQuestion: (m) => buildRunLineQuestion(m.selection, m.points, PeriodType.Period6),
		resolve: (q, gc) => resolveSpread(getPeriodScores, q, gc, PeriodType.Period6),
	},
	'7th Inning Run Line': {
		buildQuestion: (m) => buildRunLineQuestion(m.selection, m.points, PeriodType.Period7),
		resolve: (q, gc) => resolveSpread(getPeriodScores, q, gc, PeriodType.Period7),
	},
	'8th Inning Run Line': {
		buildQuestion: (m) => buildRunLineQuestion(m.selection, m.points, PeriodType.Period8),
		resolve: (q, gc) => resolveSpread(getPeriodScores, q, gc, PeriodType.Period8),
	},
	'9th Inning Run Line': {
		buildQuestion: (m) => buildRunLineQuestion(m.selection, m.points, PeriodType.Period9),
		resolve: (q, gc) => resolveSpread(getPeriodScores, q, gc, PeriodType.Period9),
	},

	// Total
	'Total Runs': {
		buildQuestion: (m) => buildTotalRunsQuestion(m.points, PeriodType.Game),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.Game),
	},
	'Total Runs Odd/Even': {
		buildQuestion: () => buildTotalRunsOddEvenQuestion(PeriodType.Game),
		isFinalRequired: true,
		resolve: (_q, gc) => resolveTotalPointsOddEven(getPeriodScores, gc, PeriodType.Game),
	},
	'1st 3 Innings Total Runs': {
		buildQuestion: (m) => buildTotalRunsQuestion(m.points, PeriodType.FirstPeriod3),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.FirstPeriod3),
	},
	'1st 7 Innings Total Runs': {
		buildQuestion: (m) => buildTotalRunsQuestion(m.points, PeriodType.FirstPeriod7),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.FirstPeriod7),
	},
	'1st Half Total Runs': {
		buildQuestion: (m) => buildTotalRunsQuestion(m.points, PeriodType.Half1),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.Half1),
	},
	'1st Inning Total Runs': {
		buildQuestion: (m) => buildTotalRunsQuestion(m.points, PeriodType.Period1),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.Period1),
	},
	'2nd Inning Total Runs': {
		buildQuestion: (m) => buildTotalRunsQuestion(m.points, PeriodType.Period2),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.Period2),
	},
	'3rd Inning Total Runs': {
		buildQuestion: (m) => buildTotalRunsQuestion(m.points, PeriodType.Period3),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.Period3),
	},
	'4th Inning Total Runs': {
		buildQuestion: (m) => buildTotalRunsQuestion(m.points, PeriodType.Period4),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.Period4),
	},
	'5th Inning Total Runs': {
		buildQuestion: (m) => buildTotalRunsQuestion(m.points, PeriodType.Period5),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.Period5),
	},
	'6th Inning Total Runs': {
		buildQuestion: (m) => buildTotalRunsQuestion(m.points, PeriodType.Period6),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.Period6),
	},
	'7th Inning Total Runs': {
		buildQuestion: (m) => buildTotalRunsQuestion(m.points, PeriodType.Period7),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.Period7),
	},
	'8th Inning Total Runs': {
		buildQuestion: (m) => buildTotalRunsQuestion(m.points, PeriodType.Period8),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.Period8),
	},
	'9th Inning Total Runs': {
		buildQuestion: (m) => buildTotalRunsQuestion(m.points, PeriodType.Period9),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.Period9),
	},

	'1st Inning Total Runs Odd/Even': {
		buildQuestion: () => buildTotalRunsOddEvenQuestion(PeriodType.Period1),
		resolve: (_q, gc) => resolveTotalPointsOddEven(getPeriodScores, gc, PeriodType.Period1),
	},
	'2nd Inning Total Runs Odd/Even': {
		buildQuestion: () => buildTotalRunsOddEvenQuestion(PeriodType.Period2),
		resolve: (_q, gc) => resolveTotalPointsOddEven(getPeriodScores, gc, PeriodType.Period2),
	},
	'3rd Inning Total Runs Odd/Even': {
		buildQuestion: () => buildTotalRunsOddEvenQuestion(PeriodType.Period3),
		resolve: (_q, gc) => resolveTotalPointsOddEven(getPeriodScores, gc, PeriodType.Period3),
	},
	'4th Inning Total Runs Odd/Even': {
		buildQuestion: () => buildTotalRunsOddEvenQuestion(PeriodType.Period4),
		resolve: (_q, gc) => resolveTotalPointsOddEven(getPeriodScores, gc, PeriodType.Period4),
	},
	'5th Inning Total Runs Odd/Even': {
		buildQuestion: () => buildTotalRunsOddEvenQuestion(PeriodType.Period5),
		resolve: (_q, gc) => resolveTotalPointsOddEven(getPeriodScores, gc, PeriodType.Period5),
	},
	'6th Inning Total Runs Odd/Even': {
		buildQuestion: () => buildTotalRunsOddEvenQuestion(PeriodType.Period6),
		resolve: (_q, gc) => resolveTotalPointsOddEven(getPeriodScores, gc, PeriodType.Period6),
	},
	'7th Inning Total Runs Odd/Even': {
		buildQuestion: () => buildTotalRunsOddEvenQuestion(PeriodType.Period7),
		resolve: (_q, gc) => resolveTotalPointsOddEven(getPeriodScores, gc, PeriodType.Period7),
	},
	'8th Inning Total Runs Odd/Even': {
		buildQuestion: () => buildTotalRunsOddEvenQuestion(PeriodType.Period8),
		resolve: (_q, gc) => resolveTotalPointsOddEven(getPeriodScores, gc, PeriodType.Period8),
	},
	'9th Inning Total Runs Odd/Even': {
		buildQuestion: () => buildTotalRunsOddEvenQuestion(PeriodType.Period9),
		resolve: (_q, gc) => resolveTotalPointsOddEven(getPeriodScores, gc, PeriodType.Period9),
	},

	// Team Total
	'Team Total': {
		buildQuestion: (m) => buildTeamTotalQuestion(m.selection, m.points, PeriodType.Game),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTeamTotal(getPeriodScores, q, gc, PeriodType.Game),
	},
	'1st 3 Innings Team Total': {
		buildQuestion: (m) => buildTeamTotalQuestion(m.selection, m.points, PeriodType.FirstPeriod3),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTeamTotal(getPeriodScores, q, gc, PeriodType.FirstPeriod3),
	},
	'1st 7 Innings Team Total': {
		buildQuestion: (m) => buildTeamTotalQuestion(m.selection, m.points, PeriodType.FirstPeriod7),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTeamTotal(getPeriodScores, q, gc, PeriodType.FirstPeriod7),
	},
	'1st Half Team Total': {
		buildQuestion: (m) => buildTeamTotalQuestion(m.selection, m.points, PeriodType.Half1),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTeamTotal(getPeriodScores, q, gc, PeriodType.Half1),
	},

	// Batter Props
	'Player Bases': {
		buildQuestion: (m) => `Will ${m.selection} record at least ${m.points} total bases?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerProp(q, gc, r, 'total_bases', ['record at least']),
	},
	'Player Doubles': {
		buildQuestion: (m) => `Will ${m.selection} record at least ${m.points} doubles?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerProp(q, gc, r, 'doubles', ['record at least']),
	},
	'Player Hits': {
		buildQuestion: (m) => `Will ${m.selection} record at least ${m.points} hits?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerProp(q, gc, r, 'hits', ['record at least']),
	},
	'Player Home Runs Yes/No': {
		buildQuestion: (m) => `Will ${m.selection} hit a home run tonight?`,
		hasProgressBar: false,
		resolve: (q, gc, r) => resolveGamePlayerProp(q, gc, r, 'home_runs', ['hit']),
	},
	'Player RBIs': {
		buildQuestion: (m) => `Will ${m.selection} record at least ${m.points} RBIs?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerProp(q, gc, r, 'runs_batted_in', ['record at least']),
	},
	'Player Singles': {
		buildQuestion: (m) => `Will ${m.selection} record at least ${m.points} singles?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => {
			const threshold = extractNumber(q);
			const playerName = extractSelection(q, 'Will ', ['record at least']);
			const playerResult = getPlayerStats(playerName, r);

			const value =
				(playerResult?.hits ?? 0) -
				(playerResult?.doubles ?? 0) -
				(playerResult?.triples ?? 0) -
				(playerResult?.home_runs ?? 0);

			return [resolveCondition(value >= threshold, gc.isCompleted), value];
		},
	},
	'Player Stolen Bases': {
		buildQuestion: (m) => `Will ${m.selection} record at least ${m.points} stolen bases?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerProp(q, gc, r, 'stolen_bases', ['record at least']),
	},
	'Player Triples': {
		buildQuestion: (m) => `Will ${m.selection} record a triple?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerProp(q, gc, r, 'triples', ['record at least'], 1),
	},
	'Player Hits + Runs + RBIs': {
		buildQuestion: (m) =>
			`Will ${m.selection} record at least ${m.points} hits, runs, and RBIs combined?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => {
			const playerName = extractSelection(q, 'Will ', ['record at least']);
			const playerResult = getPlayerStats(playerName, r);
			const value =
				(playerResult?.hits ?? 0) + (playerResult?.runs ?? 0) + (playerResult?.runs_batted_in ?? 0);
			const threshold = extractNumber(q);

			return [resolveCondition(value >= threshold, gc.isCompleted), value];
		},
	},
	'Player Home Runs': {
		buildQuestion: (m) => `Will ${m.selection} hit at least ${m.points} home runs?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerProp(q, gc, r, 'home_runs', ['hit at least']),
	},
	'Player Runs': {
		buildQuestion: (m) => `Will ${m.selection} record at least ${m.points} runs?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerProp(q, gc, r, 'runs', ['record at least']),
	},

	// Pitcher Props
	'Player Strikeouts': {
		buildQuestion: (m) => `Will ${m.selection} record at least ${m.points} strikeouts?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerProp(q, gc, r, 'strikeouts', ['record at least']),
	},
	'Player Earned Runs': {
		buildQuestion: (m) => `Will ${m.selection} allow at least ${m.points} earned runs?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerProp(q, gc, r, 'earned_runs', ['allow at least']),
	},
	'Player Hits Allowed': {
		buildQuestion: (m) => `Will ${m.selection} allow at least ${m.points} hits?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerProp(q, gc, r, 'hits_allowed', ['allow at least']),
	},
	'Player Walks': {
		buildQuestion: (m) => `Will ${m.selection} issue at least ${m.points} walks?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerProp(q, gc, r, 'walks', ['issue at least']),
	},
	'Player Outs': {
		buildQuestion: (m) => `Will ${m.selection} record at least ${m.points} outs?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerProp(q, gc, r, 'outs', ['record at least']),
	},

	// Combined Player Props
	'Player Bases (Combo)': {
		buildQuestion: (m) => {
			const [playerA, playerB] = getPairPlayers(m);

			return `Will ${playerA} and ${playerB} combine for at least ${m.points} total bases?`;
		},
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerPropCombo(q, gc, r, 'total_bases'),
	},
	'Player Home Runs (Combo)': {
		buildQuestion: (m) => {
			const [playerA, playerB] = getPairPlayers(m);

			return `Will ${playerA} and ${playerB} combine for at least ${m.points} home runs?`;
		},
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerPropCombo(q, gc, r, 'home_runs'),
	},
	'Player RBIs (Combo)': {
		buildQuestion: (m) => {
			const [playerA, playerB] = getPairPlayers(m);

			return `Will ${playerA} and ${playerB} combine for at least ${m.points} RBIs?`;
		},
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerPropCombo(q, gc, r, 'runs_batted_in'),
	},
	'Player Hits (Combo)': {
		buildQuestion: (m) => {
			const [playerA, playerB] = getPairPlayers(m);

			return `Will ${playerA} and ${playerB} combine for at least ${m.points} hits?`;
		},
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerPropCombo(q, gc, r, 'hits'),
	},
	'Player Strikeouts (Combo)': {
		buildQuestion: (m) => {
			const [playerA, playerB] = getPairPlayers(m);

			return `Will ${playerA} and ${playerB} combine for at least ${m.points} strikeouts?`;
		},
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerPropCombo(q, gc, r, 'strikeouts'),
	},

	// Home Run Props
	'Total Home Runs': {
		buildQuestion: (m) => `Will there be at least ${Math.abs(m.points)} home runs in the game?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => {
			const threshold = extractNumber(q);
			const stats = r?.score?.stats;
			const total = (stats.home?.[0]?.stats?.home_runs ?? 0) + (stats.away?.[0]?.home_runs ?? 0);

			return [resolveCondition(total >= threshold, gc.isCompleted), total];
		},
	},
	'Team Total Home Runs': {
		buildQuestion: (m) => `Will ${m.selection} hit at least ${Math.abs(m.points)} home runs?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => {
			const threshold = extractNumber(q);
			const team = extractSelection(q, 'Will ', ['hit at least']);
			const stats = r?.score?.stats;
			const isHome = team === gc.homeTeam;
			const value = isHome
				? (stats?.home?.[0]?.stats?.home_runs ?? 0)
				: (stats?.away?.[0]?.stats?.home_runs ?? 0);

			return [resolveCondition(value >= threshold, gc.isCompleted), value];
		},
	},
	// To Score / NRFI Type
	'First Team To Score': {
		buildQuestion: (m) => buildFirstTeamToScoreQuestion(m.selection, PeriodType.Game),
		resolve: (q, gc) => resolveFirstTeamToScore(getPeriodScores, q, gc, PeriodType.Game),
	},
	'1st Inning Team To Score': {
		buildQuestion: (m) => buildFirstTeamToScoreQuestion(m.selection, PeriodType.Period1),
		resolve: (q, gc) => resolveFirstTeamToScore(getPeriodScores, q, gc, PeriodType.Period1),
	},
	'2nd Inning Team To Score': {
		buildQuestion: (m) => buildFirstTeamToScoreQuestion(m.selection, PeriodType.Period2),
		resolve: (q, gc) => resolveFirstTeamToScore(getPeriodScores, q, gc, PeriodType.Period2),
	},
	'3rd Inning Team To Score': {
		buildQuestion: (m) => buildFirstTeamToScoreQuestion(m.selection, PeriodType.Period3),
		resolve: (q, gc) => resolveFirstTeamToScore(getPeriodScores, q, gc, PeriodType.Period3),
	},
	'4th Inning Team To Score': {
		buildQuestion: (m) => buildFirstTeamToScoreQuestion(m.selection, PeriodType.Period4),
		resolve: (q, gc) => resolveFirstTeamToScore(getPeriodScores, q, gc, PeriodType.Period4),
	},
	'5th Inning Team To Score': {
		buildQuestion: (m) => buildFirstTeamToScoreQuestion(m.selection, PeriodType.Period5),
		resolve: (q, gc) => resolveFirstTeamToScore(getPeriodScores, q, gc, PeriodType.Period5),
	},
	'6th Inning Team To Score': {
		buildQuestion: (m) => buildFirstTeamToScoreQuestion(m.selection, PeriodType.Period6),
		resolve: (q, gc) => resolveFirstTeamToScore(getPeriodScores, q, gc, PeriodType.Period6),
	},
	'7th Inning Team To Score': {
		buildQuestion: (m) => buildFirstTeamToScoreQuestion(m.selection, PeriodType.Period7),
		resolve: (q, gc) => resolveFirstTeamToScore(getPeriodScores, q, gc, PeriodType.Period7),
	},
	'8th Inning Team To Score': {
		buildQuestion: (m) => buildFirstTeamToScoreQuestion(m.selection, PeriodType.Period8),
		resolve: (q, gc) => resolveFirstTeamToScore(getPeriodScores, q, gc, PeriodType.Period8),
	},
	'9th Inning Team To Score': {
		buildQuestion: (m) => buildFirstTeamToScoreQuestion(m.selection, PeriodType.Period9),
		resolve: (q, gc) => resolveFirstTeamToScore(getPeriodScores, q, gc, PeriodType.Period9),
	},
	'No Runs First Inning': {
		buildQuestion: () => 'Will there be no runs in the 1st inning?',
		resolve: (_q, gc) => {
			const { home, away, isCompleted } = getPeriodScores(gc, PeriodType.Period1);

			return [
				home || away ? MarketOutcome.Lost : isCompleted ? MarketOutcome.Won : MarketOutcome.Pending,
			];
		},
	},

	// Misc Specials
	'Will There Be Overtime': {
		buildQuestion: () => `Will the game go to extra innings?`,
		resolve: (_q, gc) => resolveOvertimeGeneric(gc, PeriodType.Period9, PeriodType.Period10),
	},
	'First Home Run Type': {
		buildQuestion: (m) => `Will the first home run bring in exactly ${m.selection.toLowerCase()}?`,
		resolve: (q, gc, r) => {
			const threshold = extractNumber(q);
			const stats = r?.score?.stats;

			if (!stats) return [MarketOutcome.Pending];

			const homeHRs = stats.home?.[0]?.stats?.home_runs ?? 0;
			const awayHRs = stats.away?.[0]?.stats?.home_runs ?? 0;

			if (!homeHRs && !awayHRs)
				return gc.isCompleted ? [MarketOutcome.Lost] : [MarketOutcome.Pending];

			const firstHRStats = homeHRs ? stats.home[0].stats : stats.away[0].stats;

			return [
				(firstHRStats.runs_batted_in ?? 0) === threshold ? MarketOutcome.Won : MarketOutcome.Lost,
			];
		},
	},
};
